import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  SkeletonCircle,
  SkeletonText,
  Spinner,
  Text,
  TextProps,
} from "@chakra-ui/react"
import { Page } from "component/page/Page"
import {
  BadgeCheck,
  Brain,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  ClipboardClock,
  CloudAlert,
  FileAudio,
  FileText,
  LucideIcon,
  Medal,
  Mic,
  OctagonX,
  RotateCcw,
  TrendingUp,
  TriangleAlert,
  Upload,
} from "lucide-react"
import { notify } from "misc/notifications"
import { ChangeEvent, DragEvent, FC, ReactNode, useRef, useState } from "react"
import { useCallsAnalysisMutation } from "service/callsAnalysis/callsAnalysis"
import {
  CallAnalysisResult,
  Criteria,
  SubCriteria,
} from "type/callsAnalysis/callsAnalysis"

const SCORE_COLORS = ["green", "orange", "red"]
const SCORE_STATUSES = ["Выполнено", "Частично", "Не выполнено"]
const SCORE_ICONS = [BadgeCheck, TriangleAlert, OctagonX]

const getScoreIndex = (score?: number) => {
  let scoreIndex = 2

  if (!!score) {
    if (score >= 0.85) {
      scoreIndex = 0
    } else if (score >= 0.5) {
      scoreIndex = 1
    }
  }

  return scoreIndex
}

const results: CallAnalysisResult = {
  total_score: 0.85,
  completed_criterias: 1.7,
  call_time: 12,
  criterias: [
    {
      name: "Приветствие и знакомство с клиентом",
      score: 0.75,
      subcriterias: [
        {
          name: "Приветствие",
          description: "Менеджер должен поприветствовать клиента",
          examples: "1) Добрый день 2) Привет! 3) Здравствуйте",
          score: 0.9,
          transcript_quote: "Здравствуйте! Меня зовут Алексей",
          comment: "Отличное приветствие, вежливо и профессионально",
        },
        {
          name: "Имя клиента",
          description: "Менеджер должен узнать имя клиента",
          additional_info:
            "Можно узнать имя позже в течение диалога или если менеджер обратился по имени позже.",
          examples: "Примеры: Как могу к вам обращаться?",
          score: 0.6,
          comment:
            "Менеджер не узнал имя клиента. Рекомендуется в начале разговора спросить: 'Как могу к вам обращаться?'",
        },
      ],
    },
    {
      name: "Выявление потребностей",
      score: 0.85,
      subcriterias: [
        {
          name: "Открытые вопросы",
          description: "Менеджер должен задавать открытые вопросы",
          examples: "Примеры: Расскажите о ваших задачах, С чем сталкивается компания?",
          score: 0.9,
          transcript_quote:
            "Расскажите, пожалуйста, с какими задачами в области IT сейчас сталкивается ваша компания?",
          comment: "Отличный открытый вопрос для выявления потребностей",
        },
        {
          name: "Активное слушание",
          description:
            "Менеджер должен показать, что слушает клиента (перефразировать, уточнение, резюмирование)",
          examples: "Примеры: Правильно ли я понимаю..., То есть вы говорите...",
          score: 0.8,
          transcript_quote:
            "Понимаю, безопасность данных — это действительно критически важно",
          comment:
            "Показал понимание важности вопроса клиента, но мог бы больше переспрашивать",
        },
      ],
    },
  ],
  transcript_text: "jtiekldm ijegdfk [kwoqafndj]",
}

export const CallsAnalysis: FC = () => {
  const [uploadedFile, setUploadedFile] = useState<File>()

  const [analysisResults, setAnalysisResults] = useState<CallAnalysisResult | undefined>()

  const callsAnalysisMutation = useCallsAnalysisMutation()

  const isLoading = callsAnalysisMutation.isLoading
  const isError = callsAnalysisMutation.isError

  const isResutsExists = !!analysisResults

  const onSubmit = async () => {
    if (!uploadedFile) {
      notify("Вы не загрузили аудио файл", "error")
      return
    }

    const results = await callsAnalysisMutation.mutateAsync(uploadedFile)
    setAnalysisResults(results)
  }

  const onBack = () => {
    setAnalysisResults(undefined)
    setUploadedFile(undefined)
    callsAnalysisMutation.reset()
  }

  return (
    <Page
      bgColor="gray.50"
      direction="column"
      justifyContent="flex-start"
      alignItems="center"
      pt={10}
    >
      {/* Pretty BG */}
      <Box position="absolute" inset={0} h="100%" w="100%" overflow="hidden" zIndex={0}>
        <Box as="picture" h="100%" w="100%" display="block">
          <source
            type="image/webp"
            srcSet="https://persistent.oaistatic.com/burrito-nux/640.webp 640w, https://persistent.oaistatic.com/burrito-nux/1280.webp 1280w, https://persistent.oaistatic.com/burrito-nux/1920.webp 1920w"
          />
          <Image
            position="absolute"
            inset={0}
            h="100%"
            w="100%"
            objectFit="cover"
            alt=""
            aria-hidden="true"
            src="https://persistent.oaistatic.com/burrito-nux/640.webp"
            srcSet="https://persistent.oaistatic.com/burrito-nux/640.webp 640w, https://persistent.oaistatic.com/burrito-nux/1280.webp 1280w, https://persistent.oaistatic.com/burrito-nux/1920.webp 1920w"
            sizes="100vw"
            loading="eager"
            opacity={0.5}
            filter="blur(16px)"
            transform="scale(1.02)"
            transition="opacity 0.2s"
            userSelect="none"
            pointerEvents="none"
            draggable={false}
          />
        </Box>
        <Box
          position="absolute"
          inset={0}
          h="100%"
          w="100%"
          bgGradient="linear(to-b, transparent, white)"
          pointerEvents="none"
        />
      </Box>

      {!isResutsExists && !isLoading && !isError && (
        <UploadFileForm
          uploadedFile={uploadedFile}
          setUploadedFile={setUploadedFile}
          onSubmit={onSubmit}
        />
      )}

      {(isResutsExists || isLoading) && !isError && (
        <AnalysisResults
          results={analysisResults}
          onBack={onBack}
          isLoading={isLoading}
        />
      )}

      {isError && <ErrorAlert onBack={onBack} />}
    </Page>
  )
}

interface ErrorAlertProps {
  onBack: () => void
}

export const ErrorAlert: FC<ErrorAlertProps> = (props) => {
  const { onBack } = props

  return (
    <Page justifyContent="center" alignItems="center">
      <Flex
        bgColor="white"
        maxW="md"
        direction="column"
        justifyContent="center"
        alignItems="center"
        px={10}
        py={20}
        textAlign="center"
        borderRadius="xl"
        gap={4}
        zIndex={1}
      >
        <Icon as={CloudAlert} fontSize="8xl" color="red.300" />

        <Heading color="gray.600">Не получилось..</Heading>

        <Text color="gray.400">
          Кажется что-то пошло не так, попробуйте позже или свяжитесь с техподдержкой
        </Text>

        <IconButton
          aria-label="retry"
          icon={<RotateCcw />}
          colorScheme="gray"
          color="gray"
          mt={10}
          onClick={onBack}
        />
      </Flex>
    </Page>
  )
}

interface AnalysisResultsProps {
  results?: CallAnalysisResult
  onBack: () => void
  isLoading?: boolean
}

export const AnalysisResults: FC<AnalysisResultsProps> = (props) => {
  const { results, onBack, isLoading } = props

  const criteriasList = results?.criterias
  const transcriptText = results?.transcript_text

  const totalScore = results?.total_score
  const scoreIndex = getScoreIndex(totalScore)
  const color = SCORE_COLORS[scoreIndex]

  const completedCriterias = results?.completed_criterias
  const criteriasCount = criteriasList?.length
  const completedCriteriasStr =
    completedCriterias && criteriasCount
      ? `${completedCriterias} из ${criteriasCount}`
      : undefined

  const callTime = results?.call_time

  return (
    <Flex
      w="6xl"
      direction="column"
      justifyContent="center"
      alignItems="center"
      gap={10}
      zIndex={1}
    >
      {/* Back Btn */}
      <Button
        // variant="outline"
        colorScheme="whiteAlpha"
        color="gray.700"
        leftIcon={<ChevronLeft />}
        alignSelf="flex-start"
        onClick={onBack}
        isDisabled={isLoading}
      >
        Новый анализ
      </Button>

      {/* Header */}
      <Flex w="full" direction="column" alignItems="flex-start" gap={2}>
        <Heading>Результаты анализа</Heading>

        <Text color="gray.600">Детальная оценка эффективности звонка</Text>
      </Flex>

      {/* Cards */}
      <Flex w="full" direction="row" justifyContent="center" alignItems="center" gap={5}>
        <StatisticsCard
          icon={TrendingUp}
          title={totalScore}
          subtitle="Общая оценка"
          color={color}
          isLoading={isLoading}
        />

        <StatisticsCard
          icon={Medal}
          title={completedCriteriasStr}
          subtitle="Критериев выполнены"
          color={color}
          isLoading={isLoading}
        />

        <StatisticsCard
          icon={ClipboardClock}
          title={callTime}
          subtitle="Минут разговора"
          color="messenger"
          isLoading={isLoading}
        />
      </Flex>

      <Flex w="full" direction="row" gap={5}>
        {/* Criterias */}
        <Flex flex="2">
          <CriteriasList criteriasList={criteriasList} isLoading={isLoading} />
        </Flex>

        {/* Transcript */}
        <Flex flex="1">
          <CollapsibleTranscript text={transcriptText} isLoading={isLoading} />
        </Flex>
      </Flex>
    </Flex>
  )
}

interface CriteriasListProps {
  criteriasList?: Criteria[]
  isLoading?: boolean
}

export const CriteriasList: FC<CriteriasListProps> = (props) => {
  const { criteriasList, isLoading } = props

  return (
    <Flex
      bgColor="white"
      w="full"
      direction="column"
      px={5}
      py={8}
      borderRadius="md"
      gap={8}
    >
      <Heading size="md">Критерии оценки</Heading>

      {isLoading && <CriteriaCard isLoading={isLoading} />}

      {!isLoading &&
        criteriasList?.map((criteria) => <CriteriaCard criteria={criteria} />)}
    </Flex>
  )
}

interface CriteriaCardProps {
  criteria?: Criteria
  isLoading?: boolean
}

export const CriteriaCard: FC<CriteriaCardProps> = (props) => {
  const { criteria, isLoading } = props

  const name = criteria?.name
  const subcriterias = criteria?.subcriterias

  const score = criteria?.score
  const scoreIndex = getScoreIndex(score)

  const color = SCORE_COLORS[scoreIndex]
  const icon = SCORE_ICONS[scoreIndex]

  const isLoaded = !isLoading

  return (
    <Flex w="full" direction="column" gap={2}>
      {/* Header */}
      <Flex w="full" direction="row" alignItems="center" gap={5}>
        {/* Title */}
        <Flex w="full" direction="row" alignItems="center" gap={3}>
          {isLoaded ? (
            <Icon as={icon} color={color} fontSize="2xl" />
          ) : (
            <Spinner size="md" color="gray" />
          )}

          <Flex w="full" direction="column" gap={1}>
            <SkeletonText
              w="fit-content"
              minW="sm"
              skeletonHeight={8}
              noOfLines={1}
              isLoaded={isLoaded}
            >
              <Heading size="md">{name}</Heading>
            </SkeletonText>
          </Flex>
        </Flex>

        {/* Score */}
        <Flex direction="row" alignItems="center" gap={2}>
          <SkeletonText w="full" skeletonHeight="full" noOfLines={1} isLoaded={isLoaded}>
            <Text fontSize="2xl" fontWeight="bold" color={color} whiteSpace="nowrap">
              Оценка: {score}
            </Text>
          </SkeletonText>
        </Flex>
      </Flex>

      {/* SubCriterias */}
      <Flex w="full" direction="column" gap={2}>
        {isLoading && <SubCriteriaCard isLoading={isLoading} />}

        {!isLoading &&
          subcriterias?.map((subcriteria) => (
            <SubCriteriaCard subcriteria={subcriteria} />
          ))}
      </Flex>
    </Flex>
  )
}

interface SubCriteriaCardProps {
  subcriteria?: SubCriteria
  isLoading?: boolean
}

export const SubCriteriaCard: FC<SubCriteriaCardProps> = (props) => {
  const { subcriteria, isLoading } = props

  const name = subcriteria?.name
  const description = subcriteria?.description
  const examples = subcriteria?.examples

  const score = subcriteria?.score
  const scoreIndex = getScoreIndex(score)

  const color = SCORE_COLORS[scoreIndex]
  const badgeText = SCORE_STATUSES[scoreIndex]

  const additionalInfo = subcriteria?.additional_info
  const quote = subcriteria?.transcript_quote
  const comment = subcriteria?.comment

  const isLoaded = !isLoading

  return (
    <Flex
      w="full"
      direction="column"
      p={4}
      borderRadius="md"
      borderWidth={1}
      borderColor="gray.100"
      gap={2}
    >
      {/* Header */}
      <Flex w="full" direction="row" alignItems="flex-start" gap={5}>
        {/* Title */}
        <Flex w="full" direction="column" gap={2}>
          {/* Name */}
          <SkeletonText
            w="fit-content"
            minW="xs"
            skeletonHeight={6}
            noOfLines={1}
            isLoaded={isLoaded}
          >
            <Text fontWeight="bold">{name}</Text>
          </SkeletonText>

          {/* Description */}
          <SkeletonText
            w="fit-content"
            minW="md"
            skeletonHeight={4}
            noOfLines={1}
            isLoaded={isLoaded}
          >
            <Text fontSize="md" color="gray">
              {description}
            </Text>
          </SkeletonText>

          {/* Additional Info */}
          {!!additionalInfo && (
            <Text fontSize="sm" color="gray">
              {additionalInfo}
            </Text>
          )}

          {/* Examples */}
          <SkeletonText
            w="fit-content"
            minW="sm"
            skeletonHeight={3}
            noOfLines={1}
            isLoaded={isLoaded}
          >
            <Text fontSize="sm" color="messenger.400">
              {examples}
            </Text>
          </SkeletonText>
        </Flex>

        {/* Score */}
        <Flex
          w="fit-content"
          minW="4xs"
          direction="column"
          alignItems="flex-end"
          textAlign="end"
          gap={2}
        >
          <SkeletonText
            w="fit-content"
            minW="full"
            skeletonHeight={8}
            noOfLines={1}
            isLoaded={isLoaded}
          >
            <Badge
              h="fit-content"
              w="fit-content"
              colorScheme={color}
              borderRadius="md"
              p={1}
            >
              {badgeText}
            </Badge>
          </SkeletonText>

          <SkeletonText
            w="fit-content"
            minW="full"
            skeletonHeight={5}
            noOfLines={1}
            isLoaded={isLoaded}
          >
            <Heading size="sm" color={color} whiteSpace="nowrap">
              Оценка: {score}
            </Heading>
          </SkeletonText>
        </Flex>
      </Flex>

      {/* Transcript Quote */}
      {(!!quote || isLoading) && (
        <Flex
          bgColor="gray.100"
          w="full"
          direction="column"
          p={4}
          borderRadius="md"
          borderLeftWidth={4}
          borderLeftColor="messenger.300"
          fontSize="sm"
          whiteSpace="pre-wrap"
          gap={1}
        >
          <Text color="gray">Цитата:</Text>

          <SkeletonText w="full" skeletonHeight={4} noOfLines={2} isLoaded={isLoaded}>
            <Text fontStyle="italic">{quote}</Text>
          </SkeletonText>
        </Flex>
      )}

      {/* Comment or Recommendations */}
      <Flex
        bgColor="gray.100"
        w="full"
        direction="column"
        p={4}
        borderRadius="md"
        fontSize="sm"
        whiteSpace="pre-wrap"
        gap={1}
      >
        <Text color="gray">Комментарий и Рекомендации:</Text>

        <SkeletonText w="full" skeletonHeight={4} noOfLines={2} isLoaded={isLoaded}>
          <Text>{comment}</Text>
        </SkeletonText>
      </Flex>
    </Flex>
  )
}

interface ResultCardProps {
  icon: LucideIcon
  title?: string | number | ReactNode
  subtitle: string
  color: TextProps["colorScheme"]
  isLoading?: boolean
}

export const StatisticsCard: FC<ResultCardProps> = (props) => {
  const { icon, title, subtitle, color, isLoading } = props

  const isLoaded = !isLoading

  return (
    <Flex
      flex={1}
      bgColor="white"
      h="full"
      direction="column"
      justifyContent="center"
      alignItems="center"
      py={10}
      textAlign="center"
      borderRadius="md"
      gap={4}
    >
      {/* Icon */}
      <SkeletonCircle h="fit-content" w="fit-content" isLoaded={isLoaded}>
        <Flex
          bgColor={`${color}.100`}
          w="fit-content"
          justifyContent="center"
          alignItems="center"
          p={4}
          borderRadius="full"
        >
          <Icon as={icon} color={`${color}.500`} fontSize="2xl" />
        </Flex>
      </SkeletonCircle>

      {/* Title */}
      <SkeletonText
        w="fit-content"
        minW="3xs"
        skeletonHeight={10}
        noOfLines={1}
        isLoaded={isLoaded}
      >
        <Heading color={`${color}.500`}>{title}</Heading>
      </SkeletonText>

      {/* Subtitle */}
      <Text fontSize="sm" color="gray.500" fontWeight="medium">
        {subtitle}
      </Text>
    </Flex>
  )
}

interface CollapsibleTranscriptProps {
  text?: string
  isLoading?: boolean
}

export const CollapsibleTranscript: FC<CollapsibleTranscriptProps> = (props) => {
  const { text, isLoading } = props

  const [isOpen, setIsOpen] = useState<boolean>(true)

  const isLoaded = !isLoading

  return (
    <Flex
      bgColor="white"
      h="fit-content"
      w="full"
      direction="column"
      p={4}
      borderRadius="md"
      gap={4}
    >
      {/* Header */}
      <Flex w="full" direction="row" justifyContent="space-between" alignItems="center">
        {/* Title */}
        <Flex w="full" direction="row" alignItems="center" gap={2}>
          {isLoaded ? (
            <Icon as={FileText} fontSize="2xl" />
          ) : (
            <Spinner size="sm" color="gray" />
          )}

          <Heading size="sm">Транскрипт</Heading>
        </Flex>

        {/* Collapse Btn */}
        <IconButton
          aria-label={isOpen ? "Скрыть транскрипт" : "Показать транскрипт"}
          icon={<Icon as={isOpen ? ChevronUp : ChevronDown} boxSize={5} />}
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen((prev) => !prev)}
        />
      </Flex>

      {/* Transcript */}
      <Collapse in={isOpen} animateOpacity>
        <Flex
          w="full"
          p={4}
          bg="gray.100"
          borderRadius="md"
          boxShadow="sm"
          fontSize="sm"
          whiteSpace="pre-wrap"
        >
          <SkeletonText w="full" skeletonHeight={4} noOfLines={6} isLoaded={isLoaded}>
            <Text>{text}</Text>
          </SkeletonText>
        </Flex>
      </Collapse>
    </Flex>
  )
}

interface UploadFileFormProps {
  uploadedFile?: File
  setUploadedFile: (value: File) => void
  onSubmit: () => void
}

export const UploadFileForm: FC<UploadFileFormProps> = (props) => {
  const { uploadedFile, setUploadedFile, onSubmit } = props

  const [dragActive, setDragActive] = useState<boolean>(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const isFileUploaded = !!uploadedFile

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setDragActive(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setDragActive(false)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0]
      setUploadedFile(file)
      // handle file upload logic here
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadedFile(e.target.files[0])
    }
  }

  const handleClick = () => {
    inputRef.current?.click()
  }

  return (
    <Flex
      w="2xl"
      direction="column"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      gap={10}
      zIndex={1}
    >
      {/* Header */}
      <Flex direction="column" gap={2}>
        <Heading>Анализ звонков продаж</Heading>

        <Text fontWeight="medium" color="gray.600">
          Загрузите запись звонка для автоматического анализа эффективности продаж
        </Text>
      </Flex>

      <Flex
        bgColor="white"
        w="full"
        direction="column"
        alignItems="center"
        borderRadius="md"
        px={10}
        py={10}
        gap={8}
      >
        <Flex direction="row" alignItems="center" gap={2}>
          <Icon as={Mic} color="messenger.500" fontSize="2xl" />

          <Heading size="md"> Загрузка записи звонка</Heading>
        </Flex>

        {/* Upload File */}
        <Flex
          borderWidth={2}
          borderStyle="dashed"
          borderColor={dragActive ? "messenger.400" : "gray.300"}
          borderRadius="md"
          px={5}
          py={10}
          w="full"
          minH="120px"
          bg={dragActive ? "messenger.50" : "gray.50"}
          color="gray.600"
          textAlign="center"
          cursor="pointer"
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          direction="column"
          justifyContent="center"
          alignItems="center"
          gap={4}
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            bg="messenger.100"
            p={4}
          >
            <Icon as={FileAudio} color="messenger.500" boxSize="6" />
          </Flex>

          <Flex direction="column" gap={2}>
            <Text fontWeight="bold">Выберите MP3 файл с записью звонка</Text>
            <Text>Поддерживаются файлы в формате MP3</Text>
          </Flex>

          <Button colorScheme="gray" mt={2} leftIcon={<Upload />}>
            Выбрать файл
          </Button>

          <Input
            ref={inputRef}
            type="file"
            accept="audio/mp3"
            hidden
            onChange={handleFileChange}
          />
        </Flex>

        {/* Uploaded File */}
        {uploadedFile && (
          <Flex
            p={3}
            bg="gray.50"
            direction="row"
            alignItems="center"
            textAlign="start"
            gap={4}
            w="full"
            borderRadius="md"
          >
            <Icon as={FileAudio} color="messenger.500" fontSize="2xl" />

            <Flex direction="column" flex={1}>
              <Text fontWeight="bold" fontSize="md">
                {uploadedFile.name}
              </Text>

              <Text fontSize="sm" color="gray.500">
                {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
              </Text>
            </Flex>
          </Flex>
        )}

        <Button
          w="full"
          colorScheme="messenger"
          size="lg"
          leftIcon={<Brain />}
          onClick={onSubmit}
          isDisabled={!isFileUploaded}
        >
          Началь анализ
        </Button>
      </Flex>
    </Flex>
  )
}
